<template>
  <div
    class="flex w-full flex-col rounded-md bg-white shadow-md"
    @mouseenter.stop="tileIsHovered = true"
    @mouseleave="tileIsHovered = false"
  >
    <div class="flex items-center justify-between border-b px-6 py-4">
      <div class="flex items-center space-x-2">
        <input
          v-if="select"
          type="checkbox"
          :checked="checked"
          class="cursor-pointer"
          @input="handleCheckboxInput"
        />
        <lf-h3
          v-tooltip="scorecard.name"
          class="-mb-0 max-w-40 overflow-x-hidden text-ellipsis whitespace-nowrap"
          :class="{
            'cursor-pointer': !pageIsView,
          }"
          @click="canUpdateScoreCard && !pageIsView && openPreviewModal()"
        >
          {{ scorecard.name }}
        </lf-h3>
        <scorecard-id-badge :model-value="scorecard.id" />
        <lf-badge v-if="isManual">
          {{ $t("SCORECARDS.MANUAL") }}
        </lf-badge>
        <lf-badge v-if="isDraft">
          {{ $t("SCORECARDS.DRAFT") }}
        </lf-badge>
      </div>
      <div class="grid grid-flow-col items-center gap-x-2">
        <template v-if="isWorkflowBuilder || isWorkflowLive">
          <scorecard-favorite-button :model="scorecard" />
        </template>
        <template v-if="showEditMenu">
          <scorecard-action-menu
            :teleport="false"
            :model="scorecard"
            :offset="{ left: -147, top: 5 }"
          />
        </template>
        <template v-if="isWorkflowLive">
          <scorecard-edit-manual
            v-if="canUpdateScoreCard && isManual"
            :model="scorecard"
          />
          <run-scorecard-button
            v-else-if="canRunScoreCard"
            class="flex"
            :model="scorecard"
          />
        </template>
      </div>
    </div>
    <div class="flex flex-col space-y-5 p-6">
      <p class="line-clamp-2 text-gray-400">
        {{ scorecard.description }}
      </p>
      <div class="flex items-center justify-between">
        <span class="font-semibold text-gray-600">
          {{ $t("SCORECARDS.PTS", { points }) }}
        </span>
        <div class="flex items-center space-x-2">
          <icon-base
            v-if="getKnockout(scorecard) !== null"
            :icon="IconKnockout"
            :color="
              getKnockout(scorecard) ? UTIL_COLORS.WARNING : UTIL_COLORS.DEFAULT
            "
          />
          <scorecard-risk-tier-pill
            v-if="scorecard.results?.[0]?.score_card_risk_tier"
            :risk-tier="scorecard.results[0].score_card_risk_tier"
            font-size="text-xxs"
            readonly
          />
          <scorecard-status-pill
            v-if="scorecard.results?.[0]?.score_card_status"
            :status="scorecard.results[0].score_card_status"
            font-size="text-xxs"
            readonly
          />
        </div>
      </div>
      <scorecard-results-points-bar
        v-if="isWorkflowLive || isWorkflowBuilder"
        :categories="scorecard.results?.[0]?.categories || []"
        :total="scorecard.points || 0"
        :started="!!scorecard?.results?.length"
      />
    </div>
  </div>
  <scorecard-preview-modal
    v-if="!pageIsView && showPreviewModal && !isManual"
    :model="scorecard"
    :close="closePreviewModal"
  />
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";

import isEmpty from "lodash/isEmpty";

import { Ability, PermissionSubject } from "@/enums/auth";
import {
  SCORECARD_STATUS_DRAFT,
  SCORECARD_TYPE_MANUAL,
} from "@/helpers/constants";
import { UTIL_COLORS } from "@/helpers/constants";
import {
  ScorecardsIsBacktestReport,
  ScorecardsIsViewKey,
  ScorecardsIsWorkflowBuilder,
} from "@/helpers/symbols";
import { usePermissions } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";
import { useScorecards } from "@/hooks/scorecards";
import { useActiveStage } from "@/hooks/workflow";

import RunScorecardButton from "./RunScorecardButton.vue";
import ScorecardActionMenu from "./ScorecardActionMenu.vue";
import ScorecardEditManual from "./ScorecardEditManual.vue";
import ScorecardFavoriteButton from "./ScorecardFavoriteButton.vue";
import ScorecardIdBadge from "./ScorecardIdBadge.vue";
import ScorecardPreviewModal from "./ScorecardPreviewModal.vue";
import ScorecardResultsPointsBar from "./ScorecardResultsPointsBar.vue";
import ScorecardRiskTierPill from "./ScorecardRiskTierPill.vue";
import ScorecardStatusPill from "./ScorecardStatusPill.vue";
import IconKnockout from "@/components/icons/IconKnockout.vue";

import type { IScorecard } from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits<{
  (eventName: "tileSelect", id: string): void;
}>();

const props = defineProps({
  scorecard: {
    type: Object as PropType<IScorecard>,
    required: true,
  },
  select: {
    type: Boolean,
    default: false,
  },
  checked: {
    type: Boolean,
  },
});

const { canPerformActionReactive } = usePermissions();
const { activeStage } = useActiveStage();
const { activeDeal } = useDeals();

const pageIsView = inject(ScorecardsIsViewKey, true);
const isWorkflowBuilder = inject(ScorecardsIsWorkflowBuilder);
const isBacktestReport = inject(ScorecardsIsBacktestReport, false);

const { canUseScorecardActions } = useScorecards(false);

const tileIsHovered = ref(false);
const showPreviewModal = ref(false);

const canRunScoreCard = canPerformActionReactive(
  PermissionSubject.scoreCard,
  Ability.run,
  {
    scoreCard: props.scorecard.id,
    scoreCardStageId: activeStage.value?.id,
    application: activeDeal.value.id,
  }
);

const canUpdateScoreCard = canPerformActionReactive(
  PermissionSubject.scoreCard,
  Ability.update,
  {
    scoreCard: props.scorecard.id,
  }
);

const isWorkflowLive = !pageIsView && !isWorkflowBuilder;

const showEditMenu = computed(
  () =>
    !isWorkflowBuilder &&
    !isWorkflowLive &&
    !isBacktestReport &&
    pageIsView &&
    canUseScorecardActions.value
);

const points = computed(() => {
  if (pageIsView) {
    return props.scorecard.points || "-";
  }
  const points = props.scorecard.results?.[0]?.points || 0;
  const total = props.scorecard.points || 0;
  return `${points}/${total}`;
});

const isManual = computed(() => props.scorecard.type === SCORECARD_TYPE_MANUAL);
const isDraft = computed(
  () => props.scorecard.status === SCORECARD_STATUS_DRAFT
);

const getKnockout = (scorecard: IScorecard) => {
  const results = scorecard.results || [];
  if (!isEmpty(results) && !isEmpty(results[0].categories)) {
    return results[0]?.categories[0]?.knockout;
  }
  return null;
};

const handleCheckboxInput = () => {
  if (!props.select) {
    return;
  }
  emit("tileSelect", props.scorecard.id);
};

const closePreviewModal = () => {
  showPreviewModal.value = false;
};

const openPreviewModal = () => {
  showPreviewModal.value = true;
};
</script>
